import React from "react";

import {
  Blocks,
  Building2,
  CircleDollarSign,
  FileText,
  Fingerprint,
  FolderTree,
  ImageIcon,
  Link2,
  List,
  Package,
  Palette,
  Ruler,
  Sparkles,
  Star,
  Table2,
  Tags,
  Truck,
  Type,
  Users,
  type LucideIcon,
} from "lucide-react";

import type { AnalyzerAction, BatchFieldStatsResult, SchemaFieldInfo } from "../types";
import { StoreRouterSpinnerSvg } from "./StoreRouterSpinner";

export { StoreRouterSpinnerSvg };

export type Action = AnalyzerAction;
export type Source = "product" | "enrichment" | "brands";
export type RowTint = "product" | "enrichment" | "brands";
export type CardinalityFilterKey = "unique" | "constant" | "empty";

export type AnalyzerFieldSelection = { field: SchemaFieldInfo; source: Source };
export type FieldStatsBySource = Record<Source, BatchFieldStatsResult | null>;
export type PartitionRow = { field: SchemaFieldInfo; source: Source; indent: number };
export type SemanticBadgeTone = "slate" | "teal" | "amber" | "sky" | "violet";

export type FieldSemanticMeta = {
  badges: Array<{ label: string; tone: SemanticBadgeTone }>;
  summary: string;
  anomalyLens: string;
  suspiciousSignals: string[];
};

const OCTOGEN_SPINNER_SIZE_CLASSES = {
  lg: "size-10",
  md: "size-5",
  sm: "size-4",
  xs: "size-3",
} as const;

type OctogenSpinnerSize = keyof typeof OCTOGEN_SPINNER_SIZE_CLASSES;

export function OctogenSpinner({
  className = "",
  label = "Loading",
  size = "sm",
}: {
  className?: string;
  label?: string;
  size?: OctogenSpinnerSize;
}) {
  return (
    <StoreRouterSpinnerSvg
      label={label}
      className={`shrink-0 ${OCTOGEN_SPINNER_SIZE_CLASSES[size]} ${className}`}
    />
  );
}

function isEnrichmentField(source: Source, fieldName: string): boolean {
  return (
    source === "enrichment" || fieldName === "enrichment" || fieldName.startsWith("enrichment.")
  );
}

function getFieldLeaf(fieldName: string): string {
  return fieldName.split(".").pop()?.replace(/\[\]/g, "") ?? fieldName;
}

function getFieldIconComponent(source: Source, fieldName: string): LucideIcon {
  const leaf = getFieldLeaf(fieldName).toLowerCase();
  const normalized = fieldName.toLowerCase().replace(/\[\]/g, "");

  if (source === "brands" || leaf.includes("brand") || normalized.includes("canonical_brand")) {
    return Building2;
  }
  if (
    leaf === "offers" ||
    leaf.includes("price") ||
    leaf === "pricespecification" ||
    leaf === "lowprice" ||
    leaf === "highprice"
  ) {
    return CircleDollarSign;
  }
  if (leaf === "rating" || leaf.includes("review")) {
    return Star;
  }
  if (
    leaf === "categories" ||
    leaf === "category_path" ||
    leaf === "breadcrumblist" ||
    leaf.includes("category") ||
    leaf.includes("taxonomy")
  ) {
    return FolderTree;
  }
  if (leaf === "sizes" || leaf === "size" || leaf === "fit" || leaf === "dimensions") {
    return Ruler;
  }
  if (
    leaf === "color" ||
    leaf === "color_family" ||
    leaf === "color_info" ||
    leaf === "swatch_url" ||
    leaf.includes("color")
  ) {
    return Palette;
  }
  if (
    leaf === "audience" ||
    leaf === "gender" ||
    leaf === "age_groups" ||
    leaf.includes("audience") ||
    leaf.includes("gender")
  ) {
    return Users;
  }
  if (
    leaf === "tags" ||
    leaf === "styles" ||
    leaf === "attributes" ||
    leaf === "attribute_handles" ||
    leaf === "structured_attributes" ||
    leaf.includes("tag")
  ) {
    return Tags;
  }
  if (leaf === "materials" || leaf === "material") {
    return Blocks;
  }
  if (
    leaf === "description" ||
    leaf === "summary" ||
    leaf === "justification" ||
    leaf.includes("description")
  ) {
    return FileText;
  }
  if (leaf === "url" || leaf === "product_url" || leaf.endsWith("_url") || leaf === "sameas") {
    return Link2;
  }
  if (
    leaf.includes("image") ||
    leaf.includes("video") ||
    leaf.includes("thumbnail") ||
    leaf.includes("swatch")
  ) {
    return ImageIcon;
  }
  if (
    leaf === "availability" ||
    leaf.includes("inventory") ||
    leaf.includes("fulfillment") ||
    leaf.includes("delivery")
  ) {
    return Truck;
  }
  if (
    leaf === "catalog" ||
    leaf === "type" ||
    leaf.endsWith("_id") ||
    leaf.endsWith("_uuid") ||
    leaf.endsWith("_sha") ||
    leaf === "sku" ||
    leaf === "gtin" ||
    leaf === "upc" ||
    leaf === "ean" ||
    leaf === "mpn" ||
    leaf === "extractor_git_sha"
  ) {
    return Fingerprint;
  }
  if (leaf === "name" || leaf.endsWith("_name") || leaf === "title") {
    return Type;
  }
  if (isEnrichmentField(source, fieldName)) {
    return Sparkles;
  }
  return Package;
}

export function FieldNameIcon({
  source,
  fieldName,
  size = 12,
  className = "",
}: {
  source: Source;
  fieldName: string;
  size?: number;
  className?: string;
}) {
  const Icon = getFieldIconComponent(source, fieldName);
  return <Icon size={size} aria-hidden="true" className={className} />;
}

export function getFieldSourceLabel(source: Source, fieldName: string): string {
  if (isEnrichmentField(source, fieldName) && source === "product") {
    return "Nested enrichment";
  }
  return source === "product" ? "Base product" : source === "enrichment" ? "Enrichment" : "Brands";
}

export function getFieldSourceBadgeClass(source: Source, fieldName: string): string {
  if (isEnrichmentField(source, fieldName)) {
    return "bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-200";
  }
  if (source === "brands") {
    return "bg-violet-100 text-violet-800 dark:bg-violet-950/60 dark:text-violet-200";
  }
  return "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-200";
}

export function getFieldSemanticMeta(source: Source, fieldName: string): FieldSemanticMeta | null {
  if (!isEnrichmentField(source, fieldName)) return null;

  const enrichmentFieldName = fieldName.replace(/^enrichment\./, "");

  if (enrichmentFieldName === "attribute_handles") {
    return {
      badges: [
        { label: "Derived by Octogen", tone: "teal" },
        { label: "Taxonomy-backed", tone: "sky" },
        { label: "Search facet map", tone: "amber" },
      ],
      summary:
        "Denormalized mapping from taxonomy attribute handles to facet value handles used by search.",
      anomalyLens:
        "Treat this as normalized index data, not retailer-authored content. High combination counts can be normal, but new keys or malformed handles are suspicious.",
      suspiciousSignals: [
        "Unexpected top-level keys or missing expected keys",
        "Handle formats that do not follow attr__value conventions",
        "Sudden emptiness despite good taxonomy/category coverage",
      ],
    };
  }

  if (
    enrichmentFieldName.includes("taxonomy") ||
    enrichmentFieldName.includes("category") ||
    enrichmentFieldName === "attributes" ||
    enrichmentFieldName === "structured_attributes" ||
    enrichmentFieldName.endsWith("_handle") ||
    enrichmentFieldName.endsWith("_handles")
  ) {
    return {
      badges: [
        { label: "Derived by Octogen", tone: "teal" },
        { label: "Taxonomy-backed", tone: "sky" },
      ],
      summary: "Normalized enrichment field produced from our taxonomy/category pipeline.",
      anomalyLens:
        "Expect repeated values and a constrained value set. Cardinality spikes, unknown handles, or coverage drops are stronger signals here than uniqueness alone.",
      suspiciousSignals: [
        "Unknown categories, handles, or values",
        "Coverage drops after taxonomy/model changes",
        "Exploding distinct counts for a previously stable field",
      ],
    };
  }

  return {
    badges: [
      { label: "Derived by Octogen", tone: "teal" },
      { label: "LLM-derived", tone: "violet" },
    ],
    summary:
      "Enrichment output generated by our pipeline rather than copied from retailer source data.",
    anomalyLens:
      "Use this mostly to spot emptiness, prompt drift, inconsistent formatting, or surprising distribution changes. High uniqueness may be normal for open-ended derived fields.",
    suspiciousSignals: [
      "Coverage unexpectedly low or suddenly empty",
      "Formatting drift across values",
      "New value shapes after prompt/model changes",
    ],
  };
}

export function sourceTintForField(source: Source, fieldName: string): RowTint {
  if (source === "enrichment") return "enrichment";
  if (source === "brands") return "brands";
  return fieldName === "enrichment" || fieldName.startsWith("enrichment.")
    ? "enrichment"
    : "product";
}

export function expandKey(source: Source, fieldName: string): string {
  return `${source}:${fieldName}`;
}

export const TINT_CLASSES: Record<
  RowTint,
  { selected: string; default: string; expandHover: string }
> = {
  product: {
    selected: "bg-indigo-50 text-indigo-700 font-medium dark:bg-indigo-950/60 dark:text-indigo-200",
    default: "text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800/70",
    expandHover: "hover:bg-slate-200 dark:hover:bg-slate-700",
  },
  enrichment: {
    selected: "bg-teal-50 text-teal-700 font-medium dark:bg-teal-950/60 dark:text-teal-200",
    default: "text-teal-800 hover:bg-teal-50/60 dark:text-teal-200 dark:hover:bg-teal-950/40",
    expandHover: "hover:bg-teal-200/70 dark:hover:bg-teal-900/70",
  },
  brands: {
    selected: "bg-violet-50 text-violet-700 font-medium dark:bg-violet-950/60 dark:text-violet-200",
    default:
      "text-violet-900 hover:bg-violet-50/70 dark:text-violet-200 dark:hover:bg-violet-950/40",
    expandHover: "hover:bg-violet-200/70 dark:hover:bg-violet-900/70",
  },
};

export function getActionsForField(
  isArray: boolean
): { id: Action; label: string; icon: React.ReactNode }[] {
  return [
    ...(isArray
      ? [
          { id: "show_elements" as const, label: "Show Elements", icon: <List size={14} /> },
          {
            id: "show" as const,
            label: "Show Combinations",
            icon: <Table2 size={14} />,
          },
        ]
      : [{ id: "show" as const, label: "Show Values", icon: <List size={14} /> }]),
    { id: "list", label: "Product Sample", icon: <Table2 size={14} /> },
  ];
}

export function getSourceLabel(source: Source): string {
  return source === "product" ? "Base product" : source === "enrichment" ? "Enrichment" : "Brands";
}

const SEMANTIC_BADGE_CLASSES: Record<SemanticBadgeTone, string> = {
  slate: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
  teal: "bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-200",
  amber: "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200",
  sky: "bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-200",
  violet: "bg-violet-100 text-violet-800 dark:bg-violet-950/60 dark:text-violet-200",
};

export function SemanticBadge({ label, tone }: { label: string; tone: SemanticBadgeTone }) {
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${SEMANTIC_BADGE_CLASSES[tone]}`}
    >
      {label}
    </span>
  );
}

export function TypeBadge({ bqType }: { bqType: string }) {
  const color = bqType.startsWith("ARRAY")
    ? "bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-200"
    : bqType === "JSON"
      ? "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-200"
      : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-200";
  return <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${color}`}>{bqType}</span>;
}

export function CoverageBar({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const color = pct >= 80 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-400" : "bg-red-400";
  return (
    <div className="flex items-center gap-1.5 min-w-[72px]">
      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden dark:bg-slate-800">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] tabular-nums text-slate-400 w-[30px] text-right dark:text-slate-500">
        {pct}%
      </span>
    </div>
  );
}

const COMPACT_NUMBER_FORMATTER = new Intl.NumberFormat("en", {
  notation: "compact",
  maximumFractionDigits: 1,
});

export function getFieldCoverage(
  stats: BatchFieldStatsResult | null,
  fieldName: string
): number | null {
  if (!stats) return null;
  const count = stats.count[fieldName];
  if (count == null) return null;
  return stats.total > 0 ? count / stats.total : 0;
}

export function getFieldDistinctCount(
  stats: BatchFieldStatsResult | null,
  fieldName: string
): number | null {
  if (!stats) return null;
  const distinct = stats.count_distinct[fieldName];
  return distinct == null ? null : distinct;
}

export function getFieldPopulatedCount(
  stats: BatchFieldStatsResult | null,
  fieldName: string
): number | null {
  if (!stats) return null;
  const count = stats.count[fieldName];
  return count == null ? null : count;
}

export function getFieldUniquenessRatio(
  stats: BatchFieldStatsResult | null,
  fieldName: string
): number | null {
  const count = getFieldPopulatedCount(stats, fieldName);
  const distinct = getFieldDistinctCount(stats, fieldName);
  if (count == null || distinct == null || count === 0) return null;
  return distinct / count;
}

export function getCardinalityMeta(stats: BatchFieldStatsResult | null, fieldName: string) {
  const count = getFieldPopulatedCount(stats, fieldName);
  const distinct = getFieldDistinctCount(stats, fieldName);
  const uniqueness = getFieldUniquenessRatio(stats, fieldName);
  if (count == null || distinct == null) return null;
  if (count === 0) {
    return {
      label: "empty",
      className: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300",
      title: "No populated values",
    };
  }
  if (distinct <= 1) {
    return {
      label: "constant",
      className: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-200",
      title: "One distinct populated value",
    };
  }
  if (uniqueness != null && uniqueness >= 0.98 && count > 1) {
    return {
      label: "unique",
      className: "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-200",
      title: "Almost every populated row has a different value",
    };
  }
  return {
    label: `${COMPACT_NUMBER_FORMATTER.format(distinct)} vals`,
    className: "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-200",
    title: `${distinct.toLocaleString()} distinct populated values`,
  };
}

export function getFieldCardinalityCategory(
  stats: BatchFieldStatsResult | null,
  fieldName: string
): CardinalityFilterKey | "other" | null {
  const count = getFieldPopulatedCount(stats, fieldName);
  const distinct = getFieldDistinctCount(stats, fieldName);
  const uniqueness = getFieldUniquenessRatio(stats, fieldName);
  if (count == null || distinct == null) return null;
  if (count === 0) return "empty";
  if (distinct <= 1) return "constant";
  if (uniqueness != null && uniqueness >= 0.98 && count > 1) return "unique";
  return "other";
}

export function CardinalityBadge({
  stats,
  fieldName,
}: {
  stats: BatchFieldStatsResult | null;
  fieldName: string;
}) {
  const meta = getCardinalityMeta(stats, fieldName);
  if (!meta) return null;
  return (
    <span
      title={meta.title}
      className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${meta.className}`}
    >
      {meta.label}
    </span>
  );
}

export function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl px-5 py-3 text-center min-w-[120px] dark:border-slate-800 dark:bg-slate-900">
      <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">{value}</div>
      <div className="text-[10px] text-slate-500 mt-0.5 dark:text-slate-400">{label}</div>
    </div>
  );
}
